import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Sidebar from "@/components/layout/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Shield, Trash2, UserX, Volume2, VolumeX, Ban, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminChatControlPage = () => {
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [roomMessages, setRoomMessages] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [threadToClear, setThreadToClear] = useState("");

  const loadData = async () => {
    try {
      const response = await axios.get(`${API}/admin/chat/control-data`);
      setUsers(response.data.users || []);
      setRooms(response.data.rooms || []);
      setRoomMessages(response.data.room_messages || []);
      setDirectMessages(response.data.direct_messages || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load chat moderation data");
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const topRoomMessages = useMemo(() => roomMessages.slice(0, 25), [roomMessages]);
  const topDirectMessages = useMemo(() => directMessages.slice(0, 25), [directMessages]);

  const updateUserModeration = async (userId, payload) => {
    try {
      await axios.put(`${API}/admin/chat/users/${userId}`, payload);
      toast.success("User updated");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const deleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/admin/chat/users/${userId}`);
      toast.success("User deleted");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const removeMessage = async (messageId, type) => {
    try {
      await axios.delete(`${API}/admin/chat/messages/${messageId}`, { params: { message_type: type } });
      toast.success("Message removed");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove message");
    }
  };

  const clearRoom = async (roomId) => {
    try {
      await axios.delete(`${API}/admin/chat/rooms/${roomId}/messages`);
      toast.success("Room messages cleared");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear room");
    }
  };

  const clearThread = async () => {
    if (!threadToClear.trim()) return;
    try {
      await axios.delete(`${API}/admin/chat/direct/thread/${encodeURIComponent(threadToClear.trim())}`);
      toast.success("Direct thread cleared");
      setThreadToClear("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear direct thread");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role="admin" />
      <main className="lg:ml-64 p-4 lg:p-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">Chat Admin Control</h1>
            <p className="text-muted-foreground">Moderate users, roles, and realtime chat content.</p>
          </div>
          <Button onClick={loadData}>Refresh</Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              User Moderation
            </CardTitle>
            <CardDescription>Give roles, mute/ban users, or remove accounts.</CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[360px] pr-2">
              <div className="space-y-3">
                {users.map((u) => (
                  <div key={u.id} className="rounded-lg border border-border p-3 space-y-3">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium">{u.name}</p>
                        <p className="text-xs text-muted-foreground">{u.email} • {u.company || "-"}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{u.role}</Badge>
                        {u.chat_muted && <Badge variant="secondary">Muted</Badge>}
                        {u.chat_banned && <Badge variant="destructive">Banned</Badge>}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Select value={u.role} onValueChange={(value) => updateUserModeration(u.id, { role: value })}>
                        <SelectTrigger className="w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="driver">driver</SelectItem>
                          <SelectItem value="dispatcher">dispatcher</SelectItem>
                          <SelectItem value="distributor">distributor</SelectItem>
                          <SelectItem value="admin">admin</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button
                        variant="outline"
                        onClick={() => updateUserModeration(u.id, { chat_muted: !u.chat_muted })}
                      >
                        {u.chat_muted ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
                        {u.chat_muted ? "Unmute" : "Mute"}
                      </Button>

                      <Button
                        variant="outline"
                        onClick={() => updateUserModeration(u.id, { chat_banned: !u.chat_banned })}
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        {u.chat_banned ? "Unban" : "Ban"}
                      </Button>

                      <Button
                        variant="destructive"
                        onClick={() => deleteUser(u.id)}
                      >
                        <UserX className="w-4 h-4 mr-1" />
                        Delete User
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-primary" />
                Rooms
              </CardTitle>
              <CardDescription>Clear whole room chat history.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {rooms.map((room) => (
                <div key={room.id} className="rounded-lg border border-border p-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{room.name}</p>
                    <p className="text-xs text-muted-foreground">{room.id}</p>
                  </div>
                  <Button variant="destructive" onClick={() => clearRoom(room.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Clear Room
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Direct Threads</CardTitle>
              <CardDescription>Remove a full DM thread by thread key.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2">
                <Input
                  value={threadToClear}
                  onChange={(e) => setThreadToClear(e.target.value)}
                  placeholder="thread key (e.g. userA:userB)"
                />
                <Button variant="destructive" onClick={clearThread}>Clear Thread</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Recent Room Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[340px] pr-2">
                <div className="space-y-2">
                  {topRoomMessages.map((m) => (
                    <div key={m.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">{m.room_id} • {m.sender_name}</p>
                        <Button size="sm" variant="destructive" onClick={() => removeMessage(m.id, "room")}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <p className="text-sm mt-1">{m.content}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Recent Direct Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[340px] pr-2">
                <div className="space-y-2">
                  {topDirectMessages.map((m) => (
                    <div key={m.id} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-muted-foreground">
                          {m.sender_name} → {m.recipient_id}
                        </p>
                        <Button size="sm" variant="destructive" onClick={() => removeMessage(m.id, "direct")}>
                          <Trash2 className="w-3.5 h-3.5 mr-1" />
                          Remove
                        </Button>
                      </div>
                      <p className="text-sm mt-1">{m.content}</p>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminChatControlPage;
